import React, { Component, ErrorInfo, ReactNode } from 'react'
import { logEvent } from 'firebase/analytics'
import { analytics } from '../index'
import { getClientDiagnosticsText } from '../lib/clientDiagnostics'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  diagnosticsCopied: boolean
}

/**
 * Catches JavaScript errors in the child tree so the whole tab doesn't crash.
 * Safari's "An error repeatedly occurred" often happens when uncaught errors
 * (e.g. from third-party ad scripts) take down the page. This boundary
 * prevents one bad script from killing the entire app.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, diagnosticsCopied: false }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, diagnosticsCopied: false }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    try {
      logEvent(analytics, 'error_boundary', {
        error_message: (error.message || 'unknown').slice(0, 100),
      })
    } catch (_) {}
    if (typeof window !== 'undefined' && (window as any).clarity) {
      try {
        (window as any).clarity('set', 'error_boundary', error.message?.slice(0, 100) || 'unknown')
      } catch (_) {}
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) return this.props.fallback
      const { error } = this.state
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200">
          <p className="text-lg font-semibold mb-2">Something went wrong</p>
          {error.message && (
            <p className="text-sm font-mono text-red-600 dark:text-red-400 mb-2 text-center max-w-lg break-words">
              {error.message}
            </p>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center max-w-md">
            Please try refreshing the page. If it keeps happening, try again later.
          </p>
          <div className="flex flex-col items-center gap-2 mb-4 max-w-lg w-full">
            <button
              type="button"
              className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 font-semibold py-2 px-4 rounded text-sm"
              onClick={async () => {
                const payload = [
                  `message: ${error.message || 'unknown'}`,
                  '',
                  getClientDiagnosticsText(),
                ].join('\n')
                try {
                  await navigator.clipboard.writeText(payload)
                  this.setState({ diagnosticsCopied: true })
                  setTimeout(() => {
                    this.setState({ diagnosticsCopied: false })
                  }, 3000)
                } catch {
                  try {
                    window.prompt('Copy this text and send it to support:', payload)
                  } catch (_) {
                    /* ignore */
                  }
                }
              }}
            >
              Copy diagnostic info to clipboard
            </button>
            {this.state.diagnosticsCopied && (
              <p className="text-xs text-green-700 dark:text-green-400" role="status">
                Copied — paste into email when contacting support
              </p>
            )}
          </div>
          {error.stack && (
            <details className="w-full max-w-2xl mb-4">
              <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:underline">
                Technical details
              </summary>
              <pre className="mt-2 p-3 text-xs bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 rounded overflow-auto max-h-48 text-left">
                {error.stack}
              </pre>
            </details>
          )}
          <button
            type="button"
            className="bg-[#820458] hover:opacity-90 text-white font-bold py-2 px-4 rounded"
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
